import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  InputAdornment,
  ButtonBase,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { useAppSelector } from "@/store";
import { useGetDashboardManufacturersQuery } from "@/services";
import { useCreateFirstSaleSupplierMutation } from "@/services/first-sale";
import { roles } from "@/roles";
import { getCountryFlag } from "@/constants/country-flags";
import { tokens } from "@/tokens";
import { getInitials } from "../utils";

interface AddSupplierModalProps {
  open: boolean;
  onClose: () => void;
}

const getCountryFromMID = (mid: string): string => {
  if (mid && mid.length >= 2) return mid.substring(0, 2).toUpperCase();
  return "XX";
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

export const AddSupplierModal: React.FC<AddSupplierModalProps> = ({ open, onClose }) => {
  const [mode, setMode] = useState<"select" | "manual">("select");
  const [search, setSearch] = useState("");
  const [selectedMid, setSelectedMid] = useState<string | null>(null);
  const [selectedManufacturer, setSelectedManufacturer] = useState<IVendor | null>(null);

  // Manual entry fields
  const [manualName, setManualName] = useState("");
  const [manualMid, setManualMid] = useState("");
  const [manualFactoryName, setManualFactoryName] = useState("");
  const [manualFactoryCountry, setManualFactoryCountry] = useState("");

  // Factory fields for ERP-selected supplier
  const [erpFactoryName, setErpFactoryName] = useState("");
  const [erpFactoryCountry, setErpFactoryCountry] = useState("");

  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const [createSupplier, { isLoading: isCreating }] = useCreateFirstSaleSupplierMutation();

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const isTDAdmin = user?.userRole === roles.TDADMIN;

  const clientId = useMemo(() => {
    if (isTDAdminTCD) return selectedTenant?._id;
    if (selectedClient?._id) return selectedClient._id;
    if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) return user?._id;
    return undefined;
  }, [isTDAdminTCD, selectedTenant, selectedClient, user]);

  const { data: manufacturersData, isLoading } = useGetDashboardManufacturersQuery(
    {
      dashboardVisibility: {
        clientId,
        ...(isTDAdminTCD && selectedTenant && { tenantId: selectedTenant._id }),
      },
    },
    { skip: !clientId || !open }
  );

  const manufacturers: IVendor[] = manufacturersData?.data || [];

  const filtered = useMemo(() => {
    if (!search) return manufacturers.slice(0, 20);
    const q = search.toLowerCase();
    return manufacturers
      .filter(
        (m) =>
          (m.manufacturerName || "").toLowerCase().includes(q) ||
          m.manufacturer.toLowerCase().includes(q) ||
          (m.country || "").toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [manufacturers, search]);

  const handleClose = () => {
    setSearch("");
    setSelectedMid(null);
    setSelectedManufacturer(null);
    setMode("select");
    setManualName("");
    setManualMid("");
    setManualFactoryName("");
    setManualFactoryCountry("");
    setErpFactoryName("");
    setErpFactoryCountry("");
    onClose();
  };

  const handleAdd = async () => {
    try {
      const baseInput = {
        ...(clientId && { clientId }),
        ...(isTDAdmin && selectedTenant && { tenantId: selectedTenant._id }),
      };

      if (mode === "select" && selectedMid && selectedManufacturer) {
        const country = selectedManufacturer.country || getCountryFromMID(selectedManufacturer.manufacturer);
        await createSupplier({
          input: {
            ...baseInput,
            mid: selectedManufacturer.manufacturer,
            supplierName: selectedManufacturer.manufacturerName || selectedManufacturer.manufacturer,
            country,
            ...(erpFactoryName.trim() && { factoryName: erpFactoryName.trim() }),
            ...(erpFactoryCountry.trim() && { factoryCountry: erpFactoryCountry.trim() }),
          },
        }).unwrap();
      } else if (mode === "manual") {
        if (!manualName.trim() || !manualMid.trim()) return;
        await createSupplier({
          input: {
            ...baseInput,
            mid: manualMid.trim(),
            supplierName: manualName.trim(),
            country: getCountryFromMID(manualMid.trim()),
            ...(manualFactoryName.trim() && { factoryName: manualFactoryName.trim() }),
            ...(manualFactoryCountry.trim() && { factoryCountry: manualFactoryCountry.trim() }),
          },
        }).unwrap();
      }
      handleClose();
    } catch (err) {
      console.error("Failed to create supplier:", err);
    }
  };

  const canAdd = mode === "select" ? !!selectedMid : !!(manualName.trim() && manualMid.trim());

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: tokens.radius.lg,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: tokens.fontSize.lg,
          fontWeight: tokens.fontWeight.heavy,
          color: tokens.text.primary,
          pb: 0,
        }}
      >
        Add Supplier
      </DialogTitle>
      <DialogContent sx={{ pt: "12px !important" }}>
        {/* Mode toggle */}
        <Stack direction="row" spacing={0.75} sx={{ mb: 2 }}>
          {(["select", "manual"] as const).map((m) => (
            <ButtonBase
              key={m}
              onClick={() => setMode(m)}
              sx={{
                px: 2,
                py: 0.75,
                fontSize: tokens.fontSize.sm2,
                fontWeight: tokens.fontWeight.semibold,
                borderRadius: tokens.radius.sm,
                border: "1px solid",
                transition: "all 150ms ease",
                ...(mode === m
                  ? {
                      borderColor: tokens.text.disabled,
                      color: tokens.text.primary,
                      bgcolor: tokens.bg.surface,
                    }
                  : {
                      borderColor: "transparent",
                      color: tokens.text.secondary,
                      "&:hover": { color: tokens.text.body, bgcolor: tokens.bg.hover },
                    }),
              }}
            >
              {m === "select" ? "From Customs ERP" : "Manual Entry"}
            </ButtonBase>
          ))}
        </Stack>

        {mode === "select" && (
          <>
            <TextField
              placeholder="Search manufacturers by name, MID, or country..."
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: tokens.text.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  fontSize: tokens.fontSize.sm2,
                  borderRadius: tokens.radius.md,
                },
              }}
            />

            {/* Results list */}
            <Box
              sx={{
                border: `1px solid ${tokens.border.container}`,
                borderRadius: tokens.radius.md,
                maxHeight: 320,
                overflow: "auto",
              }}
            >
              {isLoading ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted }}>
                    Loading manufacturers...
                  </Typography>
                </Box>
              ) : filtered.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted }}>
                    {search ? "No manufacturers match your search." : "No manufacturers found."}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => setMode("manual")}
                    sx={{
                      mt: 1,
                      textTransform: "none",
                      fontSize: tokens.fontSize.sm2,
                      color: tokens.brand.primary,
                    }}
                  >
                    Add manually instead
                  </Button>
                </Box>
              ) : (
                filtered.map((m) => {
                  const country = m.country || getCountryFromMID(m.manufacturer);
                  const isSelected = selectedMid === m.manufacturer;
                  return (
                    <ButtonBase
                      key={m.manufacturer}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedMid(null);
                          setSelectedManufacturer(null);
                        } else {
                          setSelectedMid(m.manufacturer);
                          setSelectedManufacturer(m);
                        }
                      }}
                      sx={{
                        display: "flex",
                        width: "100%",
                        px: 2,
                        py: 1.25,
                        textAlign: "left",
                        borderBottom: `1px solid ${tokens.border.light}`,
                        bgcolor: isSelected ? `${tokens.brand.primary}08` : "transparent",
                        "&:hover": { bgcolor: isSelected ? `${tokens.brand.primary}12` : tokens.bg.hover },
                        "&:last-child": { borderBottom: "none" },
                        transition: "background-color 150ms ease",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: tokens.bg.hover,
                            color: tokens.brand.primary,
                            fontSize: tokens.fontSize.base,
                            fontWeight: tokens.fontWeight.bold,
                            borderRadius: tokens.radius.sm,
                          }}
                        >
                          {getInitials(m.manufacturerName || m.manufacturer)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: tokens.fontSize.sm2,
                              fontWeight: tokens.fontWeight.semibold,
                              color: tokens.text.primary,
                            }}
                          >
                            {m.manufacturerName || m.manufacturer}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography component="span" sx={{ fontSize: "0.8rem", lineHeight: 1 }}>
                              {getCountryFlag(country)}
                            </Typography>
                            <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, fontFamily: "monospace" }}>
                              {m.manufacturer}
                            </Typography>
                          </Stack>
                        </Box>
                        <Stack alignItems="flex-end" spacing={0}>
                          <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
                            {formatCurrency(m.tariffValue)}
                          </Typography>
                          <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted }}>
                            {(m.entryCount || 0).toLocaleString()} entries
                          </Typography>
                        </Stack>
                        {isSelected && (
                          <CheckIcon sx={{ fontSize: 18, color: tokens.brand.primary, ml: 1 }} />
                        )}
                      </Stack>
                    </ButtonBase>
                  );
                })
              )}
            </Box>

            {/* Factory fields for selected middleman */}
            {selectedMid && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
                  Factory Details
                </Typography>
                <TextField
                  label="Factory Name"
                  size="small"
                  fullWidth
                  value={erpFactoryName}
                  onChange={(e) => setErpFactoryName(e.target.value)}
                  placeholder="e.g., Ningbo Peikeji Paper Packaging Co., Ltd."
                  sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
                />
                <TextField
                  label="Factory Country"
                  size="small"
                  fullWidth
                  value={erpFactoryCountry}
                  onChange={(e) => setErpFactoryCountry(e.target.value)}
                  placeholder="e.g., CN"
                  sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
                />
              </Stack>
            )}
          </>
        )}

        {mode === "manual" && (
          <Stack spacing={2}>
            <TextField
              label="MID (Manufacturer ID)"
              size="small"
              fullWidth
              required
              value={manualMid}
              onChange={(e) => setManualMid(e.target.value)}
              placeholder="e.g., CNSZX12345"
              sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
            />
            <TextField
              label="Middleman Name"
              size="small"
              fullWidth
              required
              value={manualName}
              onChange={(e) => setManualName(e.target.value)}
              placeholder="e.g., Skyloyal Trade Co., Ltd."
              sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
            />
            <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary, mt: 1 }}>
              Factory Details
            </Typography>
            <TextField
              label="Factory Name"
              size="small"
              fullWidth
              value={manualFactoryName}
              onChange={(e) => setManualFactoryName(e.target.value)}
              placeholder="e.g., Ningbo Peikeji Paper Packaging Co., Ltd."
              sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
            />
            <TextField
              label="Factory Country"
              size="small"
              fullWidth
              value={manualFactoryCountry}
              onChange={(e) => setManualFactoryCountry(e.target.value)}
              placeholder="e.g., CN"
              sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md } }}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={handleClose}
          disabled={isCreating}
          sx={{
            textTransform: "none",
            fontSize: tokens.fontSize.sm2,
            color: tokens.text.secondary,
            "&:hover": { bgcolor: tokens.bg.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={!canAdd || isCreating}
          sx={{
            textTransform: "none",
            fontSize: tokens.fontSize.sm2,
            fontWeight: tokens.fontWeight.semibold,
            bgcolor: tokens.brand.primary,
            borderRadius: tokens.radius.md,
            boxShadow: "none",
            px: 3,
            "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
          }}
        >
          {isCreating ? "Adding..." : "Add Supplier"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSupplierModal;
